import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import Colors from "../../styles/Colors";
import { H1, H3 } from "../../styles/Fonts";
import { getTruncNum, nFormatter, openNewTab } from "../../utils/common";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { ClipLoader, BarLoader } from "react-spinners";
import LabelWithHint from "../../components/LabelWithHint";
import {
  setUniPoolPriceAndTvl,
  setUniPoolStatus,
} from "../../redux/factoryAction";
import { PairProps } from "../../utils/constant/pairs";
import { CHAIN_IDS_TO_NAMES } from "../../utils/constant/chains";
import PairTokenIcon from "../../components/PairTokenIcon";

const UNIV3_URL = "https://info.uniswap.org/#/";

type PairSectionProps = {
  pair: PairProps;
  openModal: () => void;
  uniPoolApr: number;
  tokenPrice: number;
  updatePoolStates: () => void;
};

const PairSection: React.FC<PairSectionProps> = (props) => {
  const { pair, openModal, uniPoolApr, tokenPrice, updatePoolStates } = props;
  const {
    src: pairIcon,
    label: pairName,
    rate,
    wantToken,
    borrowToken,
    deployedChainId,
    uniPoolAddress,
  } = pair;
  const { tokenSymbol: bTokenSymbol } = borrowToken;
  const { tokenSymbol: wTokenSymbol } = wantToken;

  const uniPoolStats = useAppSelector((state) => state.factory.uniPoolStats);
  const uniPoolState = useMemo(
    () => uniPoolStats?.[uniPoolAddress],
    [uniPoolStats, uniPoolAddress]
  );

  const dispatch = useAppDispatch();

  const handleOpenUniV3Link = (_address: string) => {
    const chainName = CHAIN_IDS_TO_NAMES[deployedChainId]?.toLowerCase();
    openNewTab(`${UNIV3_URL}${chainName}/pools/${_address}`);
  };

  useEffect(() => {
    if (!uniPoolAddress || !deployedChainId) return;

    dispatch(
      setUniPoolPriceAndTvl(
        uniPoolAddress,
        borrowToken,
        wantToken,
        deployedChainId
      )
    );
    dispatch(
      setUniPoolStatus(
        Math.floor(new Date().getTime() / 1000),
        uniPoolAddress,
        borrowToken,
        wantToken,
        deployedChainId
      )
    );

    // Update Token Prices every 10 sec
    let interval = setInterval(() => {
      updatePoolStates();
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [
    dispatch,
    uniPoolAddress,
    borrowToken,
    wantToken,
    deployedChainId,
    updatePoolStates,
  ]);

  return (
    <Container>
      <TopContainer>
        <TitleRow>
          <RairRow>
            <PairTokenIcon
              borrowTokenIcon={pair.borrowToken.tokenIcon}
              wantTokenIcon={pair.wantToken.tokenIcon}
              size="lg"
            />
            <Title>{pairName}</Title>
            <Ratio>{`${rate}%`}</Ratio>
          </RairRow>
          <PairSwitcher onClick={openModal}>Change Pair</PairSwitcher>
        </TitleRow>
        {tokenPrice ? (
          <DataRow>
            <DataCol>
              <LabelRow onClick={() => handleOpenUniV3Link(uniPoolAddress)}>
                <Label>{`Uniswap V3 ${pairName}`}</Label>
                <Icon icon={faArrowUpRightFromSquare} />
              </LabelRow>
              <PriceRow>
                <PriceText>
                  {tokenPrice ? `${getTruncNum(tokenPrice, 2)}` : "-"}
                </PriceText>
                <PriceTextUnit>{`${wTokenSymbol} per ${bTokenSymbol}`}</PriceTextUnit>
              </PriceRow>
            </DataCol>
            <DataCol>
              <LabelWithHint
                labelComponent={<Label>TVL</Label>}
                hintText="Total value locked in Uniswap WETH/USDC 0.05% pool"
              />
              <ValueText>
                {uniPoolState?.tvl
                  ? `$${nFormatter(uniPoolState?.tvl, 2)}`
                  : "-"}
              </ValueText>
            </DataCol>
            <DataCol>
              <LabelWithHint
                labelComponent={<Label>24H Vol</Label>}
                hintText="The trading volume of Uniswap WETH/USDC 0.05% pool in the last 24 hours"
              />
              <ValueText>
                {uniPoolState?.volumeIn24H ? (
                  "$" + nFormatter(uniPoolState?.volumeIn24H, 2)
                ) : (
                  <BarLoader color={Colors.ivory} width={32} />
                )}
              </ValueText>
            </DataCol>
            <DataCol>
              <LabelWithHint
                labelComponent={<Label>24H Fee</Label>}
                hintText="The fee income of Uniswap WETH/USDC 0.05% pool in the last 24 hours"
              />
              <ValueText>
                {uniPoolState?.feeIn24H ? (
                  "$" + nFormatter(uniPoolState?.feeIn24H, 2)
                ) : (
                  <BarLoader color={Colors.ivory} width={32} />
                )}
              </ValueText>
            </DataCol>
            <DataCol>
              <LabelWithHint
                labelComponent={<Label>Fee APR</Label>}
                hintText="The estimated annualized return from fee income based on the range at +-15% in the last 3 days"
              />
              <HightlightText>
                {uniPoolApr ? ((50 / 15) * uniPoolApr).toFixed(2) : "- "}%
              </HightlightText>
            </DataCol>
          </DataRow>
        ) : (
          <LoaderContainer>
            <ClipLoader color={Colors.ivory} size={24} />
          </LoaderContainer>
        )}
      </TopContainer>
    </Container>
  );
};

export default PairSection;

const Container = styled.div``;

const Title = styled(H1)`
  font-size: 24px;
  line-height: 32px;
  text-align: left;
  color: ${Colors.ivory};
  font-family: TT Firs Neue, Goldman-Sans, Helvetica Neue, Helvetica, Roboto,
    PingFang TC, 微軟雅黑, Microsoft Yahei, sans-serif;
  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 26px;
  }
  @media (max-width: 576px) {
    font-size: 18px;
    line-height: 24px;
  }
`;

const DataRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 44px;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0px;
  }
`;

const DataCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: space-between;
  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0px;
    padding: 2px 0;
  }
`;

const LabelRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const Label = styled.span`
  color: #9199a7;
  font-size: 12px;
  line-height: 18px;
  @media (max-width: 976px) {
    padding: 0;
  }
`;

const Icon = styled(FontAwesomeIcon)`
  cursor: pointer;
  font-size: 13px;
  color: ${Colors.gray3};
  :hover {
    color: ${Colors.ivory};
  }
`;

const PriceText = styled(H3)`
  color: ${Colors.ivory};
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  margin: auto auto auto 0;
  @media (max-width: 768px) {
    padding: 0;
    font-size: 14px;
    line-height: 24px;
    margin: 0;
  }
`;

const PriceTextUnit = styled(Label)`
  color: ${Colors.ivory};
`;

const HightlightText = styled(H3)`
  color: ${Colors.green};
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  margin: auto auto auto 0;
  @media (max-width: 768px) {
    padding: 0;
    font-size: 14px;
    line-height: 24px;
    margin: 0;
  }
`;

const ValueText = styled(H3)`
  min-height: 40px;
  color: ${Colors.ivory};
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  display: flex;
  align-items: center;
  @media (max-width: 768px) {
    min-height: 0px;
    padding: 0;
    font-size: 14px;
    line-height: 24px;
  }
`;

const TopContainer = styled.div`
  padding: 0 20px;
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const LoaderContainer = styled.div`
  padding: 12px 0 36px 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const RairRow = styled.div`
  display: flex;
  align-items: center;
`;

const PairSwitcher = styled.button`
  display: none;
  @media (max-width: 976px) {
    font-family: Fira Sans, Goldman-Sans, Helvetica Neue, Helvetica, Roboto,
      PingFang TC, 微軟雅黑, Microsoft Yahei, sans-serif;
    display: block;
    font-size: 14px;
    line-height: 22px;
    /* font-weight: bold; */
    color: trasparent;
    padding: 4px 16px;
    background-color: ${Colors.lightKhaki};
    border-radius: 100px;
  }
  @media (max-width: 768px) {
    font-size: 12px;
    padding: 2px 12px;
  }
`;

const Ratio = styled(Title)`
  background-color: ${Colors.backgroundGray4};
  margin-left: 8px;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 18px;
  line-height: 24px;
  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 20px;
  }
  @media (max-width: 576px) {
    font-size: 14px;
    line-height: 18px;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: flex-end;
  margin: auto auto auto 0;
  gap: 8px;
  @media (max-width: 768px) {
    align-items: center;
    justify-content: flex-end;
    margin: 0;
  }
`;
